import { Alert, Button, LoadingSpinner, Pagination, QueryErrorAlert } from '@shared/components';
import type { FundAccountPostingsModel } from '../../hooks/useFundAccountPostingsLine';
import {
    blockUnsplitNotice,
    NOT_ENTITLED_MESSAGE,
    POSTINGS_MISMATCH_MESSAGE,
    postingsRowsLabel,
    postingsUnavailableMessage,
    SUMMARIES_UNAVAILABLE_MESSAGE,
    unverifiedMessage,
} from '../../utils/fundStatementLabels';
import type { PostingsLayout } from '../../utils/postingsLayout';
import { FundAccountPostingsDataTable } from './FundAccountPostingsDataTable';
import { PostingsTotals } from './PostingsTotals';

interface Props {
    model: FundAccountPostingsModel;
    label: string;
    periodLabel: string;
    currency: string;
    layout: PostingsLayout;
    onPageChange: (page: number) => void;
}

function PostingsRows({ model, label, currency, layout, onPageChange }: Omit<Props, 'periodLabel'>) {
    const { page } = model;
    if (!page) return null;
    const perPage = page.perPage;
    const split = layout === 'split';
    return (
        <div className="space-y-2">
            {split && <p className="text-xs text-mp-muted">{postingsRowsLabel(page.rows.length, page.rowCount)}</p>}
            <FundAccountPostingsDataTable
                rows={model.displayRows}
                summaries={model.summaries}
                summariesState={model.summariesState}
                layout={layout}
                currency={currency}
            />
            <PostingsTotals label={label} layout={layout} page={page} currency={currency} />
            {page.unsplit.postings > 0 && (
                <Alert variant="warning" role="note">
                    {blockUnsplitNotice(split, page.unsplit.postings, page.unsplit.gross, currency)}
                </Alert>
            )}
            {model.summariesState === 'error' && (
                <p className="text-xs text-mp-muted">{SUMMARIES_UNAVAILABLE_MESSAGE}</p>
            )}
            {page.lastPage > 1 && (
                <Pagination
                    currentPage={page.page}
                    lastPage={page.lastPage}
                    total={page.total}
                    perPage={perPage}
                    from={(page.page - 1) * perPage + 1}
                    to={Math.min(page.page * perPage, page.total)}
                    onPageChange={onPageChange}
                />
            )}
        </div>
    );
}

export function FundAccountPostingsPanel({ model, periodLabel, ...rest }: Props) {
    switch (model.state) {
        case 'loading':
            return <LoadingSpinner message={`Loading postings for ${rest.label}`} />;
        case 'error':
            return <QueryErrorAlert variant="error" message={postingsUnavailableMessage(rest.label)} onRetry={model.onRetry} />;
        case 'unverified':
            return <QueryErrorAlert variant="warning" message={unverifiedMessage(periodLabel)} onRetry={model.onRetry} />;
        case 'not_entitled':
            return <Alert variant="info" role="note">{NOT_ENTITLED_MESSAGE}</Alert>;
        case 'mismatch':
            return (
                <Alert variant="warning" role="status">
                    <div className="space-y-2">
                        <p>{POSTINGS_MISMATCH_MESSAGE}</p>
                        <Button type="button" size="xs" variant="neutral" appearance="outline" onClick={model.onReload}>
                            Reload
                        </Button>
                    </div>
                </Alert>
            );
        default:
            return <PostingsRows model={model} {...rest} />;
    }
}
